import type { CalcKey } from './keys';

export const en: Record<CalcKey, string> = {
  // Common controls
  'panel.params': 'Parameters',
  'panel.advanced': 'Advanced',
  'action.reset': 'Reset',
  'action.randomise': 'Randomise',
  'action.reset-view': 'Reset view',
  // App chrome
  'app.eyebrow': 'Maths · Calculus',
  // Experiment selector
  'panel.experiment': 'Experiment',
  // Experiment labels
  'experiment.limit-lab': 'Limit lab',
  'experiment.tangent-lab': 'Tangent lab',
  'experiment.derivative-plotter': 'Derivative plotter',
  'experiment.applications': 'Applications',
  'experiment.riemann-lab': 'Riemann lab',
  'experiment.ftc-lab': 'FTC lab',
  'experiment.taylor-lab': 'Taylor lab',
  'experiment.slope-field-lab': 'Slope field lab',
  // Limit presets
  'preset.limit.removable': 'Removable hole',
  'preset.limit.jump': 'Jump discontinuity',
  'preset.limit.infinite': 'Infinite discontinuity',
  'preset.limit.oscillating': 'Oscillating discontinuity',
  'preset.limit.continuous': 'Continuous (control)',
  // Tangent presets
  'preset.tangent.parabola': 'Parabola x²',
  'preset.tangent.sine': 'Sine sin(x)',
  'preset.tangent.cubic': 'Cubic x³ − 3x',
  'preset.tangent.exponential': 'Exponential eˣ',
  // Limit-lab panel labels
  'limit.panel.preset': 'Function',
  'limit.panel.approach': 'Approach x',
  'limit.panel.epsilon': 'ε (epsilon)',
  'limit.panel.show-epsilon-delta': 'Show ε–δ band',
  // Limit-lab readout labels
  'limit.readout.left': 'lim x→a⁻',
  'limit.readout.right': 'lim x→a⁺',
  'limit.readout.fa': 'f(a)',
  'limit.readout.kind': 'kind',
  // Limit-lab info / help
  'experiment.limit-lab.info':
    'Drag the cursor along the x-axis to probe the limit. The readout shows the one-sided limits and classifies the discontinuity at the approach point.',
  // Tangent-lab panel labels
  'tangent.panel.preset': 'Function',
  'tangent.panel.a': 'Point a',
  'tangent.panel.h': 'Step h',
  'tangent.panel.mode': 'Quotient type',
  // Tangent-lab mode values
  'tangent.mode.forward': 'Forward',
  'tangent.mode.symmetric': 'Symmetric',
  // Tangent-lab info / help
  'experiment.tangent-lab.info':
    'Drag P to move the base point and Q to change the step h. Watch the secant line approach the tangent as h → 0. The readout shows [f(a+h)−f(a)]/h converging to f′(a).',
  // Derivative presets
  'preset.derivative.cubic': 'Cubic x³ − 3x',
  'preset.derivative.sine': 'Sine sin(x)',
  'preset.derivative.parabola': 'Parabola x²',
  'preset.derivative.bell': 'Bell curve e^(−x²)',
  // Application presets
  'preset.application.open-box': 'Open box volume',
  'preset.application.rect-under-line': 'Rectangle under a line',
  'preset.application.ladder': 'Sliding ladder',
  'preset.application.ripple': 'Expanding ripple',
  // Derivative-plotter panel labels
  'derivative.panel.preset': 'Function',
  'derivative.panel.cursor-x': 'Cursor x',
  // Derivative-plotter info
  'experiment.derivative-plotter.info':
    'Drag the cursor along the x-axis. The top pane shows f with a tangent segment; the bottom pane traces f′ — the tie-dot shows how the tangent slope equals the derivative value. Advanced adds f″ and concavity shading.',
  // Applications panel labels
  'applications.panel.preset': 'Scenario',
  'applications.panel.param': 'Parameter',
  'applications.panel.mode': 'Mode',
  // Applications mode values
  'applications.mode.optimization': 'Optimization',
  'applications.mode.related-rates': 'Related rates',
  // Applications info
  'experiment.applications.info':
    "Optimization: drag the parameter to see the geometric scenario change and the quantity curve rise and fall — the marker's tangent goes flat at the optimum. Related rates: observe how dx/dt and dy/dt vectors relate through the chain rule.",
  // Integral presets
  'preset.integral.polynomial': 'Polynomial x²',
  'preset.integral.sine': 'Sine sin(x)',
  'preset.integral.cosine': 'Cosine cos(x)',
  'preset.integral.exponential': 'Exponential eˣ',
  'preset.integral.linear': 'Linear 2x + 1',
  'preset.integral.reciprocal': 'Reciprocal 1/x',
  // Riemann-lab panel labels
  'riemann.panel.preset': 'Function',
  'riemann.panel.n': 'Subintervals n',
  'riemann.panel.rule': 'Rule',
  // Riemann-lab rule values
  'riemann.rule.left': 'Left',
  'riemann.rule.right': 'Right',
  'riemann.rule.mid': 'Midpoint',
  'riemann.rule.trap': 'Trapezoid',
  // Riemann-lab info / help
  'experiment.riemann-lab.info':
    'Adjust n to subdivide [a, b] into more rectangles and watch the Riemann sum Σ converge to the exact integral ∫. The readout shows the signed sum, the curated antiderivative value, and the error |Σ − ∫|. Advanced mode unlocks the midpoint and trapezoid rules.',
  // FTC-lab panel labels
  'ftc.panel.preset': 'Function',
  'ftc.panel.x': 'Upper limit x',
  'ftc.panel.show-second-ftc': 'Show 2nd FTC (numeric check)',
  // FTC-lab info / help
  'experiment.ftc-lab.info':
    'Drag x to accumulate area under f from a to x. The top pane shades the area ∫ₐˣ f; the bottom pane traces A(x) = F(x) − F(a) with a tie-dot showing the current value. The readout surfaces the curated antiderivative F and the FTC identity A′(x) = f(x). Advanced mode compares the exact A(x) to the numeric area.',
  // Taylor presets
  'preset.taylor.exp': 'Exponential eˣ',
  'preset.taylor.sin': 'Sine sin(x)',
  'preset.taylor.cos': 'Cosine cos(x)',
  'preset.taylor.ln1px': 'Logarithm ln(1+x)',
  'preset.taylor.geometric': 'Geometric 1/(1−x)',
  'preset.taylor.atan': 'Arctangent atan(x)',
  // Taylor-lab panel labels
  'taylor.panel.preset': 'Function',
  'taylor.panel.degree': 'Degree n',
  'taylor.panel.center': 'Center a',
  // Taylor-lab info / help
  'experiment.taylor-lab.info':
    'Drag the center point along the x-axis to move the expansion center. Increase the degree slider to add more terms — watch Pₙ(x) hug f near the center and diverge outside the shaded interval of convergence. The readout shows the exact polynomial and its coefficients.',
  // Slope-field presets
  'preset.slope-field.exponential-growth': 'Exponential growth y′ = y',
  'preset.slope-field.linear-x': 'Linear slope y′ = x',
  'preset.slope-field.circles': 'Concentric circles y′ = −x/y',
  'preset.slope-field.linear-ode': 'Linear ODE y′ = x − y',
  // Slope-field-lab panel labels
  'slope-field.panel.preset': 'Equation dy/dx',
  'slope-field.panel.step-h': 'Step h',
  'slope-field.panel.show-family': 'Show solution family',
  // Slope-field-lab info / help
  'experiment.slope-field-lab.info':
    "Drag the initial condition point to thread a solution curve through the slope field. In advanced mode, decrease h to tighten Euler's error and reveal the family of solution curves. The faint overlay shows the closed-form solution where available.",
};
